import * as React from 'react';
import { useObservable } from 'react-use-observable';

import { Button, Col, Row } from 'antd';

import { cpf, cnpj } from 'cpf-cnpj-validator';
import { Space } from 'v2/components/Shared/Space';
import financialService from 'v2/services/financial';

import { CAIXA_ECONOMICA_ID, PersonalDocumentType } from '../../form';
import type { NewBankAccount } from '../../form';

type DataDisplayProps = {
  values: NewBankAccount;
  onPreviewClick: (fieldName: string) => void;
};

function DataDisplay({ values, onPreviewClick }: DataDisplayProps) {
  const [banks] = useObservable(() => financialService.getBanks(), []);

  const isDocumentCpf = cpf.isValid(values.document_number);

  function getDocumentName() {
    if (values.document_image_type === PersonalDocumentType.RG) return 'RG';
    if (values.document_image_type === PersonalDocumentType.CNH) return 'CNH';
    return 'Identidade profissional';
  }

  if (!banks) return null;

  const selectedBank = banks?.find(bank => bank.id === values.bank_id);
  const bankName = selectedBank?.value.replace(/[0-9]/g, '').trim();

  return (
    <Row className='-mx-2 mt-4 mb-6' gutter={[16, 16]}>
      <Col span={24}>
        <h4 className='mb-2 text-base text-[#072D83] uppercase'>Dados pessoais</h4>
        <div className='rounded border border-[#ECEFF1] bg-white p-5'>
          <p className='mb-[5px]! text-base leading-[150%] font-semibold text-[#37474F]' data-testid='name-label'>
            Nome
          </p>
          <p className='m-0! text-sm leading-[150%] text-[#78909C]' data-testid='name-value'>
            {values.name}
          </p>
          <Space axis='vertical' size={16} />
          <p className='mb-[5px]! text-base leading-[150%] font-semibold text-[#37474F]' data-testid='document-label'>
            {isDocumentCpf ? 'CPF' : 'CNPJ'}
          </p>
          <p className='m-0! text-sm leading-[150%] text-[#78909C]' data-testid='document-value'>
            {isDocumentCpf ? cpf.format(values.document_number) : cnpj.format(values.document_number)}
          </p>
        </div>
      </Col>
      {(values.document_image_front || values.document_image_back) && (
        <Col span={24}>
          <h4 className='mb-2 text-base text-[#072D83] uppercase'>Documentos</h4>

          <div className='rounded border border-[#ECEFF1] bg-white p-5'>
            <p
              className='mb-[5px]! text-base leading-[150%] font-semibold text-[#37474F]'
              data-testid='personal-document-label'
            >
              {getDocumentName()}
            </p>

            <Space axis='vertical' size={16} />

            <div className='flex flex-wrap gap-2'>
              <Button id='document-image-front-preview-btn' onClick={() => onPreviewClick('document_image_front')}>
                {values.document_image_type === PersonalDocumentType.RG && 'Visualizar frente RG'}
                {values.document_image_type === PersonalDocumentType.CNH && 'Visualizar CNH'}
                {values.document_image_type === PersonalDocumentType.PROFESSIONAL &&
                  'Visualizar frente Identidade Profissional'}
              </Button>

              {[PersonalDocumentType.RG, PersonalDocumentType.PROFESSIONAL].includes(values.document_image_type) && (
                <Button id='document-image-back-preview-btn' onClick={() => onPreviewClick('document_image_back')}>
                  {values.document_image_type === PersonalDocumentType.RG && 'Visualizar verso RG'}
                  {values.document_image_type === PersonalDocumentType.PROFESSIONAL &&
                    'Visualizar verso Identidade Profissional'}
                </Button>
              )}
            </div>
          </div>
        </Col>
      )}
      {values.selfie_video && (
        <Col span={24}>
          <h4 className='mb-2 text-base text-[#072D83] uppercase'>Vídeo</h4>
          <div className='border border-[#ECEFF1] bg-white p-5'>
            <p className='mb-[5px]! text-base leading-[150%] font-semibold text-[#37474F]'>Vídeo selfie</p>
            <Space axis='vertical' size={16} />
            <Button id='video-preview-btn' onClick={() => onPreviewClick('selfie_video')}>
              Visualizar vídeo
            </Button>
          </div>
        </Col>
      )}
      <Col span={24}>
        <h4 className='mb-2 text-base text-[#072D83] uppercase'>Conta bancária</h4>
        <div className='border border-[#ECEFF1] bg-white p-5'>
          <p
            className='mb-[5px]! text-base leading-[150%] font-semibold text-[#37474F]'
            data-testid='account-type-label'
          >
            <strong>{values.type === 'CC' ? 'Conta corrente' : 'Conta poupança'}</strong>
          </p>
          <Space axis='vertical' size={16} />
          <p className='mb-[5px]! text-base leading-[150%] font-semibold text-[#37474F]' data-testid='bank-name-value'>
            {bankName}
          </p>
          <Space axis='vertical' size={8} />
          <div className='text-sm leading-[150%] text-[#37474F]' data-testid='bank-data-value'>
            <p className='m-0!'>Agência: {values.agency}</p>
            {values.agency_digit && <p>Dígito da agência: {values.agency_digit}</p>}
            <p className='m-0!'>Conta: {values.account}</p>
            <p className='m-0!'>Dígito da conta: {values.account_digit}</p>
            {values.bank_id === CAIXA_ECONOMICA_ID && <p className='m-0!'>Operação: {values.operation_id}</p>}
          </div>
          {values.bank_receipt_image && (
            <>
              <Space axis='vertical' size={16} />
              <Button id='bank-receipt-preview-btn' onClick={() => onPreviewClick('bank_receipt_image')}>
                Visualizar comprovante
              </Button>
            </>
          )}
        </div>
      </Col>
    </Row>
  );
}

export { DataDisplay };
